'use client';

import { Icons } from '@/components/icons';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { useCallback, useMemo, useState } from 'react';
import { Input } from '../../ui/input';
import { useDebounceCallback } from 'usehooks-ts';
import { presets } from './presets';
import { useWorkflows } from '@/providers/workflow-provider';
import { toast } from 'sonner';
import {
  createEmptyUserBookmarks,
  createEmptyWorkEngagementBookmarks,
} from '@/lib/bookmark/utils/bookmark-utils';
import type {
  UserBookmarks,
  WorkEngagementBookmarks,
} from '@/lib/bookmark/types';
import { LoaderButton } from '@/components/ui/loader-button';
import { getMaxRootFolderOrder, workflowPresetToFolder } from './utils';

interface WorkflowPresetsModalProps {
  readonly onOpenChange: (open: boolean) => void;
}

export function WorkflowPresetsModal({
  onOpenChange,
}: WorkflowPresetsModalProps) {
  const { save, workflows, isSaving, isWorkEngagementBookmarksContext } =
    useWorkflows();
  const [selectedPresetIds, setSelectedPresetIds] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const debounced = useDebounceCallback(setSearch, 500);

  const filteredPresets = useMemo(() => {
    const q = search.toLowerCase();
    return presets.filter(
      (preset) =>
        preset.name.toLowerCase().includes(q) ||
        preset.description.toLowerCase().includes(q)
    );
  }, [search]);

  const presetById = useMemo(() => {
    return new Map(presets.map((p) => [p.presetId, p]));
  }, []);

  const togglePreset = (presetId: string) => {
    setSelectedPresetIds((prev) =>
      prev.includes(presetId)
        ? prev.filter((id) => id !== presetId)
        : [...prev, presetId]
    );
  };

  const handleSave = useCallback((): void => {
    if (selectedPresetIds.length === 0) {
      toast.info('Välj minst ett arbetsflöde att lägga till');
      return;
    }

    const emptyBase = isWorkEngagementBookmarksContext
      ? createEmptyWorkEngagementBookmarks()
      : createEmptyUserBookmarks();

    const existingFolders = workflows?.folders ?? emptyBase.folders;
    let nextOrder = getMaxRootFolderOrder(existingFolders) + 1;
    const foldersToAdd = selectedPresetIds.flatMap((id) => {
      const preset = presetById.get(id);
      if (!preset) {
        return [];
      }
      const folder = workflowPresetToFolder(preset);
      const withOrder = { ...folder, order: nextOrder };
      nextOrder += 1;
      return [withOrder];
    });

    const merged = {
      ...(workflows ?? emptyBase),
      folders: [...existingFolders, ...foldersToAdd],
    };

    const savePromise = save(merged as UserBookmarks | WorkEngagementBookmarks);

    toast.promise(savePromise, {
      loading: 'Sparar arbetsflöden...',
      success: () => {
        setSelectedPresetIds([]);
        onOpenChange(false);
        return 'Arbetsflöden har lagts till';
      },
      error: 'Kunde inte spara arbetsflöden',
    });
  }, [
    isWorkEngagementBookmarksContext,
    onOpenChange,
    presetById,
    save,
    selectedPresetIds,
    workflows,
  ]);

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className='p-0' size='md'>
        <DialogHeader className='flex-col items-start gap-1'>
          <div className='flex w-full flex-row items-center justify-between'>
            <DialogTitle>Färdiga arbetsflöden från Pigello</DialogTitle>
            <Button
              className='items-end'
              variant='outline'
              size='icon-sm'
              onClick={() => onOpenChange(false)}
            >
              <Icons.close className='size-4' />
            </Button>
          </div>
          <DialogDescription>
            Du kan också skapa egna arbetsflöden genom att spara dina favoriter
            genom att klicka på{' '}
            <Icons.bookmarkAdd className='inline-block size-4' /> uppe till
            höger på sidan du vill spara.
          </DialogDescription>
        </DialogHeader>
        <div className='flex h-[500px] flex-col'>
          <div className='p-4'>
            <Input
              className='w-80'
              placeholder={'Sök bland arbetsflöden...'}
              defaultValue=''
              onChange={(event) => {
                debounced(event.target.value);
              }}
            />
          </div>
          <div className='h-full overflow-y-auto border-t'>
            {filteredPresets.map((preset) => {
              const isSelected = selectedPresetIds.includes(preset.presetId);

              return (
                <div
                  key={preset.presetId}
                  role='button'
                  tabIndex={-1}
                  onClick={() => togglePreset(preset.presetId)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      togglePreset(preset.presetId);
                    }
                  }}
                  className='flex w-full cursor-pointer items-center justify-between gap-4 border-b p-4 transition-colors last:border-b-0 hover:bg-accent'
                >
                  <div>
                    <div className='mb-1 flex items-center gap-2 font-medium'>
                      {preset.name}
                    </div>
                    <p className='text-xs text-muted-foreground'>
                      {preset.description}
                    </p>
                  </div>
                  <Button
                    className='shrink-0'
                    variant={isSelected ? 'default' : 'outline'}
                  >
                    {isSelected ? 'Tillagd' : 'Lägg till'}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
        <DialogFooter>
          <Button
            disabled={isSaving}
            variant='outline'
            onClick={() => onOpenChange(false)}
          >
            Avbryt
          </Button>
          <LoaderButton
            isLoading={isSaving}
            disabled={isSaving}
            onClick={() => void handleSave()}
          >
            Spara
          </LoaderButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
